import { isNil } from "lodash-es";
import ReconnectingWebSocket from "reconnecting-websocket";

import {
  PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE,
  type PubsubVirtualClassStateMessageFromServerType,
} from "../shared/types/av-state";
import {
  type GroupPreviewSnapshotMessageType,
  PAGE_GROUP_PREVIEW_MESSAGE_TYPE,
} from "../shared/types/page-group-preview";
import type {
  ClientPubsubModelUpdateType,
  ModelUpdateMessageType,
  PubsubAuthUpdateConnectMessage,
  PubsubMessageType,
} from "../shared/types/pubsub";
import { type WebsocketErrorType } from "../shared/types/websocket-auth";
import { type AppDispatch, store, storeApi } from "../store/index";
import { clearPubsubAPICache } from "../store/pubsub-slice";
import { selectCurrentSectionId, selectCurrentUserId } from "../store/selectors/base";
import { selectSectionVersion } from "../store/selectors/view";
import { authStateActions } from "../store/slices/auth";
import { interimStateActions } from "../store/slices/interim";
import { viewStateActions } from "../store/slices/view";

import { BROADCAST_TOPICS, sendBroadcast } from "./broadcast";
import { isMoreThanXMinutesAgo } from "./date";
import { logInfoWithDebugFlag, rollbarAndLogError, rollbarAndLogWarning } from "./logger";
import { getWindowId } from "./window";

import { handlePubsubVirtualClassStateMessage } from "components/av/virtual-class-utils";

// Keep this in sync with SECTION_MESSAGE_QUEUE_MAX_LENGTH from material/tasks--this
// is the most messages we'll store in our redis queue for missed pubsub messages.
const MAX_REDIS_SECTION_VERSION_DIFF = 500;

// This is used to track the last time we did a full refresh of the page.
// We use this to determine if we should reload the page due to a large version diff.
const LAST_FULL_REFRESH_DATE_KEY = "ForumPubsubLastFullRefreshDate";
const MIN_TIME_BETWEEN_FULL_REFRESHES_MIN = 5;
const STATE_MAP: Record<number, string> = {
  0: "Connecting",
  1: "Open",
  2: "Closing",
  3: "Closed",
};

/**
 * Fetch the live section (and its Redis section_version). Until this resolves,
 * pings send 0 so we don't replay the queue against a stale version.
 */
export const refreshSectionFromServer = async (sectionId: string, dispatch: AppDispatch) => {
  clearPubsubAPICache();

  const section = await storeApi.sections.retrieve(sectionId, {
    skipToast: true,
  });

  logInfoWithDebugFlag("debug_pubsub", "Attempting to set new section version", {
    section_version: section.section_version,
    section_id: section.id,
    windowId: getWindowId(),
    userId: selectCurrentUserId(store.getState()),
  });

  if (selectCurrentSectionId(store.getState()) === sectionId) {
    dispatch(viewStateActions.setSectionVersionTo(section.section_version));
  }

  return section;
};

/**
 * Handles auth update messages
 */
/** Connect-time identity only (Redis auth-updates are not sent to the browser). */
export const handleAuthMessage = (
  message: PubsubAuthUpdateConnectMessage,
  dispatch: AppDispatch,
) => {
  dispatch(authStateActions.setWebsocketUserData(message.data));
};

/**
 * Handles model update messages
 */
export const handleModelMessage = (
  message: ClientPubsubModelUpdateType,
  sectionId: string | null,
  dispatch: AppDispatch,
) => {
  const checkAndIncrementSectionVersion = () => {
    const { section_version: newSectionVersion, section_id: msgSectionId } = message;
    const sectionVersion = selectSectionVersion(store.getState());

    if (
      !msgSectionId ||
      isNil(newSectionVersion) ||
      sectionId !== msgSectionId ||
      newSectionVersion === sectionVersion
    )
      return true;

    // Local 0 means we are waiting on retrieve. Apply the update; do not reload
    // or ping the server with 0 (it ignores that). Sequencing resumes after
    // refreshSectionFromServer sets the live version.
    if (sectionVersion === 0) {
      return true;
    }

    const versionDiff = newSectionVersion - sectionVersion;
    // Only actually increment the section version if it's the next version
    // being applied
    if (versionDiff === 1) {
      dispatch(viewStateActions.setSectionVersionTo(newSectionVersion));
      return true;
    }

    if (versionDiff < 0) {
      // If we receive a version from an API call that is _lower_ than the current one,
      // it likely means that in between the server calculating the response it coming back to us,
      // we also received a pubsub message with a version increase. In this case,
      // we ignore the returned version value, since we've already correctly incremented beforehand.
      // We still set isSectionVersionOutOfSync to true, just to ensure we send a message to double-check
      // the version on the server. This should be a no-op, just a sanity check.
      logInfoWithDebugFlag(
        "debug_pubsub",
        "Received a version from an API call that is lower than the current one",
        {
          sectionId,
          newSectionVersion,
          oldSectionVersion: sectionVersion,
        },
      );
      // TODO: Will this do anything useful?
      dispatch(viewStateActions.setIsSectionVersionOutOfSync(true));
    } else if (versionDiff > MAX_REDIS_SECTION_VERSION_DIFF) {
      const lastFullRefreshDate = localStorage.getItem(LAST_FULL_REFRESH_DATE_KEY);

      if (
        !lastFullRefreshDate ||
        isMoreThanXMinutesAgo(lastFullRefreshDate, MIN_TIME_BETWEEN_FULL_REFRESHES_MIN)
      ) {
        // If we are way out of sync, reload the page to get the latest data
        rollbarAndLogWarning(`Reloading due to large version diff`, {
          sectionId,
          newSectionVersion,
          oldSectionVersion: sectionVersion,
        });
        localStorage.setItem(LAST_FULL_REFRESH_DATE_KEY, new Date().toISOString());

        // We could make this more subtle by clearing the store and only remounting
        // the root component to load all our data, but this shouldn't happen often
        // enough to warrant the complexity.  We can change if that's not the case.
        window.location.reload();
      } else {
        rollbarAndLogWarning(`Attempting to reload too frequently`, {
          sectionId,
          newSectionVersion,
          oldSectionVersion: sectionVersion,
          lastFullRefreshDate,
        });
      }
    } else {
      dispatch(viewStateActions.setIsSectionVersionOutOfSync(true));
    }

    return false;
  };

  const isValidMessage = checkAndIncrementSectionVersion();

  if (isValidMessage && "model" in message) {
    sendBroadcast(BROADCAST_TOPICS.PUBSUB_MODEL_UPDATE, message);
  }
};

/**
 * Sets up socket event listeners for pubsub messages
 */
export const setupSocketListeners = (
  socket: ReconnectingWebSocket,
  {
    sectionId,
    userId,
    dispatch,
    onError,
  }: {
    sectionId: string | null;
    userId: string | null;
    dispatch: AppDispatch;
    onError: (errorCode: "401" | null) => void;
  },
) => {
  socket.addEventListener("error", (error) => {
    const errorMessage = error?.message || error || "Unknown error";
    const state = (error?.target?.readyState || socket.readyState) as string;
    const errorText = `WebSocket error${errorMessage ? `: ${errorMessage as string}` : ""}`;

    const errorLogger =
      errorMessage === "TIMEOUT" ||
      (typeof errorMessage === "object" &&
        JSON.stringify(errorMessage) === JSON.stringify({ isTrusted: true }))
        ? console.warn
        : rollbarAndLogWarning;

    errorLogger(errorText, {
      timestamp: new Date().toISOString(),
      userId,
      sectionId,
      state: STATE_MAP[state] ?? state,
    });
  });

  socket.addEventListener("message", (evt: MessageEvent) => {
    try {
      if (typeof evt.data !== "string") {
        rollbarAndLogError("Socket got message evt.data of unexpected type", typeof evt.data);
        return;
      }

      const data = JSON.parse(evt.data) as
        | PubsubMessageType
        | PubsubVirtualClassStateMessageFromServerType
        | GroupPreviewSnapshotMessageType
        | { type: "error"; error: WebsocketErrorType };

      if (data?.type === PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE) {
        handlePubsubVirtualClassStateMessage(data, dispatch);
        return;
      }

      if (data?.type === PAGE_GROUP_PREVIEW_MESSAGE_TYPE && data.action === "snapshot") {
        dispatch(interimStateActions.setPageGroupPreviewSnapshot(data));
        return;
      }

      const messages = data?.type === "pubsub" ? data.messages : [];

      for (const message of messages) {
        if (message.type && message.type === "auth-update") {
          // Adding some additional logging to see why auth update are not being logged
          logInfoWithDebugFlag("debug_pubsub", "Received auth message", {
            sectionId,
            userId,
            messageType: data?.type,
            message: JSON.stringify(message).substring(0, 100),
          });

          handleAuthMessage(message, dispatch);
        } else if (message.type && message.type === "model-update") {
          logInfoWithDebugFlag("debug_pubsub", "Received model message", {
            sectionId,
            userId,
            messageType: data?.type,
            model: (message as ModelUpdateMessageType).model,
            message: JSON.stringify(message).substring(0, 100),
          });

          handleModelMessage(message, sectionId, dispatch);
        } else if (message.type && message.type === "attendance-update") {
          dispatch(interimStateActions.updateUserActivity(message));
        }
      }

      if ("error" in data && data.error) {
        const code = data.error.code;
        // All of these send rollbar messages on the backend, so we don't need to do that here
        if (code === 403) {
          // An operation was rejected due to missing permissions. This shouldn't happen,
          // but should also not be fatal. We leave it to ShareDBDoc to handle this.
          // TODO: We might want to briefly flash a small "error" connection indicator state once
          // we have the more subdued connection indicator design in place, just to give visual indications
          // for small things going wrong.
        } else if (code === 401) {
          // The JWT token is invalid. This is fatal, so we close the connection. We don't
          // log the user out forcefully to prevent login loops.
          onError("401");
        } else {
          // If the error is critical, ShareDB will re-try connecting for a bit, and if not
          // successful, it will close the connection, which consumers can react to by listening
          // to the shareDBStatus.
        }
      }
    } catch (err) {
      console.error("Error reading socket data", err, evt.data);
    }
  });
};
